import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Search, Loader2, X } from 'lucide-react';
import { PhoneHQInput } from "ch3-ui-lib";
import {matrixService} from "../../services/matrixService.ts";
import {formatLastMessageTime} from "../../hooks/useMatrix.ts";
import {MatrixAvatar} from "./MatrixAvatar.tsx";
import {findMatchRanges, matchesSearchTerm} from "../../utilis/searchMatch.ts";

interface FormattedSearchResult {
    eventId: string;
    roomId: string;
    sender: string;
    timestamp: number;
    body: string;
    msgtype: string;
    profile: Record<string, any>;
}

interface MatrixMessageSearchProps {
    selectedRoomId: string;
    onResultClick?: (result: string) => void;
    debounceMs?: number;
    setShowSearchPanel: (show: boolean) => void;
    onSearchQueryChange?: (query: string) => void;
}

const LOAD_MORE_COUNT = 100;

const MatrixMessageSearch: React.FC<MatrixMessageSearchProps> = ({
                                                                     setShowSearchPanel,
                                                                     selectedRoomId,
                                                                     onResultClick,
                                                                     debounceMs = 500,
                                                                     onSearchQueryChange
                                                                 }) => {
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<FormattedSearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [hasSearched, setHasSearched] = useState(false);

    const searchTimeline = useCallback((query: string): FormattedSearchResult[] => {
        const trimmed = query.trim();
        if (!trimmed || trimmed.length < 2 || !selectedRoomId) return [];

        const client = matrixService.getClient();
        const room = client?.getRoom(selectedRoomId);
        const messages = matrixService.getRoomMessages(selectedRoomId, 50000);

        return messages
            .filter(event => {
                if (event.getType() !== 'm.room.message' || event.isRedacted()) return false;
                const body = event.getContent()?.body;
                return Boolean(body) && typeof body === 'string' && matchesSearchTerm(body, trimmed);
            })
            .map(event => {
                const content = event.getContent();
                const senderId = event.getSender() || '';
                let displayName = senderId;
                if (room) {
                    const member = room.getMember(senderId);
                    if (member?.rawDisplayName) displayName = member.rawDisplayName;
                }

                return {
                    eventId: event.getId() || '',
                    roomId: selectedRoomId,
                    sender: senderId,
                    timestamp: event.getTs(),
                    body: content.body,
                    msgtype: content.msgtype || 'm.text',
                    profile: {
                        [senderId]: {
                            displayname: displayName,
                            avatar_url: room?.getMember(senderId)?.getMxcAvatarUrl() || undefined,
                        }
                    }
                };
            })
            .sort((a, b) => b.timestamp - a.timestamp);
    }, [selectedRoomId]);

    const performSearch = useCallback((query: string) => {
        const trimmed = query.trim();
        if (!trimmed || trimmed.length < 2) {
            setSearchResults([]);
            setHasSearched(!trimmed ? false : true);
            return;
        }

        setIsSearching(true);
        const results = searchTimeline(query);
        setSearchResults(results);
        setHasSearched(true);
        setIsSearching(false);
    }, [searchTimeline]);

    const handleLoadMore = useCallback(async () => {
        if (isLoadingMore || !selectedRoomId) return;

        setIsLoadingMore(true);
        try {
            const result = await matrixService.loadOlderMessages(selectedRoomId, LOAD_MORE_COUNT);
            setHasMore(result.messages.length > 0);

            if (searchQuery.trim().length >= 2) {
                const results = searchTimeline(searchQuery);
                setSearchResults(results);
            }
        } catch (error) {
            console.error('Failed to load older messages:', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [isLoadingMore, selectedRoomId, searchQuery, searchTimeline]);

    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([]);
            setHasSearched(false);
            return;
        }

        const timeoutId = setTimeout(() => {
            performSearch(searchQuery);
        }, debounceMs);

        return () => clearTimeout(timeoutId);
    }, [searchQuery, performSearch, debounceMs]);

    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const handleLoadMoreRef = useRef(handleLoadMore);
    handleLoadMoreRef.current = handleLoadMore;

    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    handleLoadMoreRef.current();
                }
            },
            { threshold: 0 }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasSearched, hasMore, searchResults]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onResultClick) onResultClick('');
        const value = e.target.value;
        setSearchQuery(value);
        onSearchQueryChange?.(value);
    };

    const handleInputFocus = () => {
        if (!showSearch) setShowSearch(true);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            if (onResultClick) onResultClick('');
            setShowSearch(false);
            setSearchQuery('');
            onSearchQueryChange?.('');
            setSearchResults([]);
            setHasSearched(false);
        }
    };

    const highlightSearchTerm = (text: string, searchTerm: string) => {
        if (!searchTerm.trim() || !text) return text;

        // Highlight from the same ranges the match was found on, so an accent
        // insensitive hit still underlines the accented original. The previous
        // regex could not do that, and reused a /g/ regex across .test() calls,
        // whose lastIndex made it skip every other match.
        const ranges = findMatchRanges(text, searchTerm.trim());
        if (ranges.length === 0) return text;

        const parts: React.ReactNode[] = [];
        let cursor = 0;

        ranges.forEach(({start, end}, index) => {
            if (start > cursor) parts.push(text.slice(cursor, start));
            parts.push(
                <mark key={`${start}-${index}`} className="font-semibold bg-yellow-200 px-0.5 rounded">
                    {text.slice(start, end)}
                </mark>
            );
            cursor = end;
        });

        if (cursor < text.length) parts.push(text.slice(cursor));

        return parts;
    };

    const getDisplayName = (result: FormattedSearchResult): string => {
        const senderProfile = result.profile?.[result.sender];
        if (senderProfile?.displayname) return senderProfile.displayname;
        const match = result.sender.match(/@([^:]+):/);
        return match ? match[1] : result.sender;
    };

    return (
        <div className=" mb-3">
            <>
                <div className="w-80 h-full bg-surface border-l border-line flex flex-col transition-all duration-300 ease-in-out">
                <div className="pl-5 py-4 pr-3 border-b border-line flex items-center justify-between h-[72px]">
                                    <h3 className="text-base font-semibold text-ink">Search</h3>
                    <button
                        onClick={() => {
                            if (onResultClick) onResultClick('');
                            setShowSearchPanel(false);
                        }}
                        className="text-ink-muted hover:text-ink-secondary transition-colors"
                    >
                        <X className="button p-1.5" size={28}/>
                    </button>
                </div>
                <div className=''>
                    {isSearching && (
                        <div className="p-4 flex items-center justify-center">
                            <Loader2 className="animate-spin mr-2" size={16} />
                            Searching...
                        </div>
                    )}
                <div className='relative w-full mb-2 p-2'>
                    <PhoneHQInput
                        type="search"
                        value={searchQuery}
                        readOnly={false}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onKeyDown={handleKeyDown}
                        className='w-full pl-10 py-2 bg-surface-muted outline:none focus:ring-2 focus:bg-surface rounded-lg box-shadow-none border-0 ring-0 placeholder:text-ink-muted text-tiny font-medium'
                        placeholder={'Search in chat...'}
                    />
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-muted">
                                <Search className={'text-ink-muted'} size={18} />
                    </div>
                </div>

                    {!isSearching && searchResults.length === 0 && hasSearched && (
                        <div className="p-4 text-ink-muted text-sm text-center">
                            <div>No messages found for "{searchQuery}"</div>
                            <div className="text-xs mt-1 text-ink-muted">
                                Try a different search term or load older messages
                            </div>
                        </div>
                    )}

                        <>
                            {Boolean(searchResults.length) && <div className="text-xs text-ink-muted py-2 px-3">
                                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                            </div>}
                            <div className={'h-[calc(100vh-140px)] overflow-y-auto'}>
                                 {searchResults.map((result, index) => (
                                     <div key={`${result.eventId}-${index}`} className={'flex w-full p-2.5 hover:bg-canvas transition-colors mx-[2px] rounded-xl'}>
                                         <MatrixAvatar
                                             size={'32px'}
                                             name={getDisplayName(result)}
                                             mxcUrl={result.profile?.[result.sender]?.avatar_url}
                                         />
                                         <div
                                             className="px-3 border-b last:border-b-0 cursor-pointer w-full"
                                             onClick={() => onResultClick?.(result.eventId)}
                                         >
                                             <div className="flex justify-between items-start leading-4">
                                                <span className="text-tiny font-medium text-ink truncate">
                                                    {getDisplayName(result)}
                                                </span>
                                             </div>
                                             <div className={"flex items-center"}>
                                                 <div className="text-tiny text-ink-muted line-clamp-2">
                                                     {highlightSearchTerm(result.body, searchQuery)}
                                                 </div>
                                                 <span className="text-tiny text-ink-muted-500 ml-2 flex-shrink-0">
                                                {formatLastMessageTime(result.timestamp)}
                                            </span>
                                             </div>
                                         </div>
                                     </div>
                                 ))}

                                 {hasSearched && hasMore && (
                                     <div ref={loadMoreSentinelRef} className="py-3 px-2.5 flex items-center justify-center">
                                         {isLoadingMore && (
                                             <div className="flex items-center gap-1.5 text-tiny font-medium text-ink-muted">
                                                 <Loader2 className="animate-spin" size={14} />
                                                 Loading...
                                             </div>
                                         )}
                                     </div>
                                 )}
                            </div>
                        </>
                </div>
                </div>
            </>
        </div>
    );
};

export default MatrixMessageSearch;
